(() => {
  const namespace = '__locSpecularButtons';
  if (window[namespace]) {
    window[namespace].scan(document);
    return;
  }

  const PAD = 20;
  const settings = {
    radius: 18,
    lineColor: [1, 1, 1],
    baseColor: [0x52 / 255, 0x52 / 255, 0x52 / 255],
    intensity: 1,
    shineSize: (10 * Math.PI) / 180,
    shineFade: (40 * Math.PI) / 180,
    thickness: 1,
    speed: 0.35,
    followMouse: true,
    proximity: 250,
    autoAnimate: false,
  };

  const vertexShaderSource = `#version 300 es
    in vec2 position;
    void main() {
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  const fragmentShaderSource = `#version 300 es
    precision highp float;

    uniform vec2 uCenter;
    uniform vec2 uHalfSize;
    uniform float uRadius;
    uniform float uAngle;
    uniform float uPx;
    uniform vec3 uLineColor;
    uniform vec3 uBaseColor;
    uniform float uIntensity;
    uniform float uShineSize;
    uniform float uShineFade;
    uniform float uThickness;
    uniform float uBaseWidth;

    out vec4 fragColor;

    float sdRoundedRect(vec2 p, vec2 b, float r) {
      vec2 q = abs(p) - b + r;
      return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
    }

    float gaussianLine(float d, float sigma) {
      float x = d / (sigma + 1e-6);
      float k = mix(1.0, 1.6, smoothstep(0.0, 1.5, x));
      return exp(-k * x * x);
    }

    void main() {
      vec2 p = gl_FragCoord.xy - uCenter;
      float d = sdRoundedRect(p, uHalfSize, uRadius);
      vec2 lightDirection = vec2(cos(uAngle), sin(uAngle));
      // A firmer neutral rim keeps the white highlight legible on pale pages.
      float base = (1.0 - smoothstep(0.0, uBaseWidth, abs(d))) * 0.78;
      vec2 ellipticalNormal = normalize(p / (uHalfSize * uHalfSize) + 1e-6);
      float phi = acos(clamp(abs(dot(ellipticalNormal, lightDirection)), 0.0, 1.0));
      float rim = 1.0 - smoothstep(uShineSize - uShineFade, uShineSize + uShineFade + 1e-4, phi);
      float line = gaussianLine(d, uThickness);
      float edgeClamp = 1.0 - smoothstep(0.5 * uPx, 3.0 * uPx, abs(d));
      float highlight = line * rim * edgeClamp * uIntensity;
      float contrastHalo = gaussianLine(d, uThickness * 2.2) * rim * edgeClamp * uIntensity * 0.2;
      vec3 color = uBaseColor * base + uBaseColor * contrastHalo + uLineColor * highlight;
      float alpha = clamp(base + contrastHalo + highlight, 0.0, 1.0);
      fragColor = vec4(color, alpha);
    }
  `;

  const instances = new Set();
  const pointer = { active: false, x: 0, y: 0 };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animationFrame = 0;
  let lastFrameTime = performance.now();

  const compileShader = (gl, type, source) => {
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader;
    console.warn('Light of Cards specular shader error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  };

  const createProgram = (gl) => {
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    if (!program) return null;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program;
    console.warn('Light of Cards specular program error:', gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  };

  const render = (instance) => {
    const { gl, program, uniforms } = instance;
    if (!instance.width || !instance.height || gl.isContextLost()) return;

    gl.useProgram(program);
    gl.uniform1f(uniforms.angle, instance.angle);
    gl.uniform1f(uniforms.intensity, settings.intensity * instance.brightness);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  const resize = (instance) => {
    const rect = instance.element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const computedRadius = Number.parseFloat(getComputedStyle(instance.element).borderTopLeftRadius);
    const radius = Number.isFinite(computedRadius) ? computedRadius : settings.radius;
    const cssWidth = rect.width + PAD * 2;
    const cssHeight = rect.height + PAD * 2;
    const pixelWidth = Math.max(1, Math.round(cssWidth * dpr));
    const pixelHeight = Math.max(1, Math.round(cssHeight * dpr));

    if (instance.canvas.width !== pixelWidth || instance.canvas.height !== pixelHeight) {
      instance.canvas.width = pixelWidth;
      instance.canvas.height = pixelHeight;
    }

    instance.width = rect.width;
    instance.height = rect.height;
    instance.gl.viewport(0, 0, pixelWidth, pixelHeight);
    instance.gl.useProgram(instance.program);
    instance.gl.uniform2f(instance.uniforms.center, (PAD + rect.width / 2) * dpr, (PAD + rect.height / 2) * dpr);
    instance.gl.uniform2f(instance.uniforms.halfSize, (rect.width / 2) * dpr, (rect.height / 2) * dpr);
    instance.gl.uniform1f(instance.uniforms.radius, Math.min(radius, rect.width / 2, rect.height / 2) * dpr);
    instance.gl.uniform1f(instance.uniforms.pixel, dpr);
    instance.gl.uniform1f(instance.uniforms.thickness, settings.thickness * dpr);
    instance.gl.uniform1f(instance.uniforms.baseWidth, dpr);
    render(instance);
  };

  const initialise = (element) => {
    if (element.dataset.locSpecularReady === 'true') return;
    element.dataset.locSpecularReady = 'true';

    const effect = Array.from(element.children).find((child) => child.classList?.contains('loc-specular__fx'));
    if (!effect) {
      element.dataset.locSpecularFallback = 'true';
      return;
    }

    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      powerPreference: 'low-power',
    });

    if (!gl) {
      element.dataset.locSpecularFallback = 'true';
      return;
    }

    const program = createProgram(gl);
    if (!program) {
      element.dataset.locSpecularFallback = 'true';
      return;
    }

    const position = gl.getAttribLocation(program, 'position');
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(program);

    const uniforms = {
      center: gl.getUniformLocation(program, 'uCenter'),
      halfSize: gl.getUniformLocation(program, 'uHalfSize'),
      radius: gl.getUniformLocation(program, 'uRadius'),
      angle: gl.getUniformLocation(program, 'uAngle'),
      pixel: gl.getUniformLocation(program, 'uPx'),
      lineColor: gl.getUniformLocation(program, 'uLineColor'),
      baseColor: gl.getUniformLocation(program, 'uBaseColor'),
      intensity: gl.getUniformLocation(program, 'uIntensity'),
      shineSize: gl.getUniformLocation(program, 'uShineSize'),
      shineFade: gl.getUniformLocation(program, 'uShineFade'),
      thickness: gl.getUniformLocation(program, 'uThickness'),
      baseWidth: gl.getUniformLocation(program, 'uBaseWidth'),
    };

    gl.uniform3fv(uniforms.lineColor, settings.lineColor);
    gl.uniform3fv(uniforms.baseColor, settings.baseColor);
    gl.uniform1f(uniforms.shineSize, settings.shineSize);
    gl.uniform1f(uniforms.shineFade, settings.shineFade);

    effect.appendChild(canvas);
    const instance = {
      element,
      effect,
      canvas,
      gl,
      program,
      buffer,
      uniforms,
      width: 0,
      height: 0,
      angle: 2.4,
      targetAngle: 2.4,
      brightness: 0,
      targetBrightness: 0,
      resizeObserver: null,
    };

    instance.resizeObserver = new ResizeObserver(() => resize(instance));
    instance.resizeObserver.observe(element);
    instances.add(instance);
    resize(instance);
  };

  const destroy = (instance) => {
    instance.resizeObserver?.disconnect();
    instance.gl.deleteBuffer(instance.buffer);
    instance.gl.deleteProgram(instance.program);
    instance.gl.getExtension('WEBGL_lose_context')?.loseContext();
    instance.canvas.remove();
    instance.element.removeAttribute('data-loc-specular-ready');
    instances.delete(instance);
  };

  const scheduleAnimation = () => {
    if (animationFrame || reducedMotion.matches) return;
    lastFrameTime = performance.now();
    animationFrame = requestAnimationFrame(animate);
  };

  const animate = (now) => {
    const delta = Math.min((now - lastFrameTime) / 1000, 0.05);
    lastFrameTime = now;
    let keepAnimating = false;

    instances.forEach((instance) => {
      if (!instance.element.isConnected) {
        destroy(instance);
        return;
      }

      const angleDifference = ((instance.targetAngle - instance.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      const angleEase = 1 - Math.exp(-delta * 7);
      const brightnessEase = 1 - Math.exp(-delta * 8);
      instance.angle += angleDifference * angleEase;
      instance.brightness += (instance.targetBrightness - instance.brightness) * brightnessEase;
      render(instance);

      if (Math.abs(angleDifference) > 0.001 || Math.abs(instance.targetBrightness - instance.brightness) > 0.002) {
        keepAnimating = true;
      } else {
        instance.angle = instance.targetAngle;
        instance.brightness = instance.targetBrightness;
        render(instance);
      }
    });

    animationFrame = keepAnimating ? requestAnimationFrame(animate) : 0;
  };

  const updateFromPointer = () => {
    if (!pointer.active || reducedMotion.matches || !settings.followMouse) return;

    instances.forEach((instance) => {
      const rect = instance.element.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const outsideX = Math.max(rect.left - pointer.x, 0, pointer.x - rect.right);
      const outsideY = Math.max(rect.top - pointer.y, 0, pointer.y - rect.bottom);
      const distance = Math.hypot(outsideX, outsideY);

      if (distance === 0) {
        const normalX = (pointer.x - centerX) / Math.max(rect.width / 2, 1);
        const normalY = (centerY - pointer.y) / Math.max(rect.height / 2, 1);
        instance.targetAngle = Math.atan2(2 / rect.height, -2 / rect.width) + normalX * 0.3 + normalY * 0.15;
      } else {
        instance.targetAngle = Math.atan2(centerY - pointer.y, pointer.x - centerX);
      }

      const proximity = Math.max(0, 1 - distance / Math.max(settings.proximity, 1));
      instance.targetBrightness = proximity * proximity * (3 - 2 * proximity);
    });

    scheduleAnimation();
  };

  const fadeAll = () => {
    pointer.active = false;
    instances.forEach((instance) => {
      instance.targetBrightness = 0;
    });
    scheduleAnimation();
  };

  const scan = (scope) => {
    if (!scope) return;
    if (scope.matches?.('[data-loc-specular]')) initialise(scope);
    scope.querySelectorAll?.('[data-loc-specular]').forEach(initialise);
  };

  window.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    pointer.active = true;
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    updateFromPointer();
  }, { passive: true });

  window.addEventListener('scroll', updateFromPointer, { passive: true });
  window.addEventListener('resize', () => {
    instances.forEach(resize);
    updateFromPointer();
  }, { passive: true });
  window.addEventListener('blur', fadeAll);
  document.documentElement.addEventListener('mouseleave', fadeAll);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance) => {
      if (event.target === instance.element || event.target.contains(instance.element)) destroy(instance);
    });
  });

  reducedMotion.addEventListener?.('change', (event) => {
    if (event.matches) fadeAll();
  });

  window[namespace] = { scan };
  scan(document);
})();
